<main *inexpFeatureFlag="SNOW_RENDERER_FLAG" class="catalog-form">
  @if (item()) {
    <h1 class="sr-only">Catalog request form: {{ title() }}</h1>
  }

  @if (state() === 'loading') {
    <output class="catalog-form__spinner-overlay" aria-live="polite">
      <rds-progress-spinner aria-hidden="true"></rds-progress-spinner>
      <span class="ui-body-l">{{ CATALOG_FORM_TEXTS.LOADING_MESSAGE }}</span>
    </output>
  }

  <section class="catalog-form__left" aria-label="Request form">
    <div class="catalog-form__back">
      <button
        rds-text-button
        size="s"
        type="button"
        [disabled]="isSubmitting()"
        (click)="goBack()"
      >
        <rds-icon namespace="filled" icon="arrow_left" aria-hidden="true" />
        {{ CATALOG_FORM_TEXTS.BACK_BUTTON }}
      </button>
    </div>
    <!-- aria-busy sits here, not on <main>, so Back is not inside a busy region. -->
    <div class="catalog-form__body" [attr.aria-busy]="state() === 'loading'">
    @switch (state()) {
      @case ('error') {
        <h2 #stateHeading tabindex="-1" class="catalog-form__title">
          @if (notEntitledReasons().length > 0) {
            {{ CATALOG_FORM_TEXTS.ERROR_TITLE_NOT_ENTITLED }}
          } @else {
            {{ CATALOG_FORM_TEXTS.READY_TITLE }}
          }
        </h2>
        <div class="catalog-form__state-card">
          <rds-alert type="error">
            @if (notEntitledReasons().length > 0) {
              <ul class="catalog-form__reasons">
                @for (reason of notEntitledReasons(); track reason) {
                  <li>{{ reason }}</li>
                }
              </ul>
            } @else {
              {{ errorMessage() ?? CATALOG_FORM_TEXTS.DEFAULT_ERROR_MESSAGE }}
            }
          </rds-alert>
        </div>
        @if (notEntitledReasons().length === 0) {
          <div class="catalog-form__submit-container">
            <button rds-primary-button size="s" type="button" (click)="retry()">
              {{ CATALOG_FORM_TEXTS.RETRY_BUTTON }}
            </button>
          </div>
        }
      }
      @case ('submitted') {
        <!-- Focus lands on this heading; aria-describedby makes the screen
             reader read the confirmation + reference number with it. -->
        <h2
          #stateHeading
          tabindex="-1"
          class="catalog-form__title"
          aria-describedby="catalog-form-submitted-message"
        >
          {{ CATALOG_FORM_TEXTS.SUBMITTED_TITLE }}
        </h2>
        <div class="catalog-form__state-card">
          <rds-alert type="success">
            <span id="catalog-form-submitted-message">
              @if (submitMessage()) {
                {{ submitMessage() }}
              } @else {
                {{ CATALOG_FORM_TEXTS.SUBMITTED_FALLBACK_MESSAGE }}
                @if (ritmNumber()) {
                  {{ CATALOG_FORM_TEXTS.SUBMITTED_REFERENCE_LABEL }}
                  <strong>{{ ritmNumber() }}</strong>
                }
              }
            </span>
          </rds-alert>
        </div>
        <div class="catalog-form__submit-container">
          <button rds-primary-button size="s" type="button" (click)="reset()">
            {{ CATALOG_FORM_TEXTS.SUBMIT_ANOTHER_BUTTON }}
          </button>
        </div>
      }
      @case ('ready') {
        <h2 #stateHeading tabindex="-1" class="catalog-form__title">
          {{ CATALOG_FORM_TEXTS.READY_TITLE }}
        </h2>
        <form
          class="catalog-form__form"
          [formGroup]="form"
          (ngSubmit)="submit()"
          novalidate
        >
          <div class="catalog-form__scroll">
            <!-- Always rendered so the notice, which can appear mid-form, is
                 announced: live regions inserted with their content often are not. -->
            <div role="status">
              @if (dynamicValuesUnavailable()) {
                <rds-alert type="warning">
                  {{ CATALOG_FORM_TEXTS.DYNAMIC_VALUES_UNAVAILABLE }}
                </rds-alert>
              }
            </div>
            @if (formErrors().length > 0) {
              <rds-alert type="error" role="alert">
                @for (message of formErrors(); track message) {
                  <p class="catalog-form__form-error">{{ message }}</p>
                }
              </rds-alert>
            }
            @for (section of sections(); track section.title ?? $index) {
              <section class="catalog-form__section">
                @if (section.title) {
                  <h3 class="catalog-form__section-title ui-heading-3-bold">
                    {{ section.title }}
                  </h3>
                }
                @for (variable of section.fields; track variable.name) {
                  @switch (variable.renderType) {
                    @case ('label') {
                      @if (variable.html) {
                        <!-- Rich text label: Angular's sanitiser strips scripts / inline styles, keeps links. -->
                        <div class="catalog-form__rich-text ui-body-l" [innerHTML]="variable.html"></div>
                      } @else {
                        <p class="catalog-form__label-field ui-body-l">
                          {{ variable.label }}
                        </p>
                      }
                    }
                    @case ('checkbox') {
                      <div class="catalog-form__checkbox-field">
                        <rds-checkbox
                          [formControlName]="variable.name"
                          [required]="variable.mandatory"
                        >
                          {{ variable.label }}
                        </rds-checkbox>
                        @if (variable.helpText) {
                          <span class="catalog-form__hint" [innerHTML]="variable.helpText"></span>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <span class="catalog-form__checkbox-error" role="alert">
                            {{ CATALOG_FORM_TEXTS.REQUIRED_FIELD_ERROR }}
                          </span>
                        }
                      </div>
                    }
                    @case ('radio') {
                      <fieldset
                        class="catalog-form__radio-field"
                        [attr.aria-describedby]="
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                            ? variable.name + '-error'
                            : null
                        "
                      >
                        <legend class="catalog-form__radio-legend">
                          {{ variable.label }}
                          @if (variable.mandatory) {
                            <span class="catalog-form__required-mark" aria-hidden="true">*</span>
                            <span class="sr-only">{{ CATALOG_FORM_TEXTS.REQUIRED_SR }}</span>
                          }
                        </legend>
                        <rds-radio-group
                          [class.catalog-form__radio-group--across]="variable.choiceDirection === 'across'"
                          [formControlName]="variable.name"
                          [name]="variable.name"
                          [required]="variable.mandatory"
                        >
                          @for (choice of variable.choices; track choice.value) {
                            @if (choice.value !== '') {
                              <rds-radio-button [value]="choice.value">
                                {{ choice.label }}
                              </rds-radio-button>
                            }
                          }
                        </rds-radio-group>
                        @if (variable.helpText) {
                          <span class="catalog-form__hint" [innerHTML]="variable.helpText"></span>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <span
                            class="catalog-form__checkbox-error"
                            role="alert"
                            [id]="variable.name + '-error'"
                          >
                            {{ CATALOG_FORM_TEXTS.REQUIRED_CHOICE_ERROR }}
                          </span>
                        }
                      </fieldset>
                    }
                    @case ('select') {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <rds-select
                          [formControlName]="variable.name"
                          [required]="variable.mandatory"
                        >
                          @for (choice of variable.choices; track choice.value) {
                            <rds-select-option [value]="choice.value">
                              {{ choice.label }}
                            </rds-select-option>
                          }
                        </rds-select>
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_CHOICE_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('textarea') {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <textarea
                          rdsInput
                          rows="4"
                          [formControlName]="variable.name"
                          [attr.placeholder]="variable.placeholder"
                          [attr.maxlength]="variable.maxLength"
                          [required]="variable.mandatory"
                          [attr.aria-label]="variable.label"
                        ></textarea>
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_FIELD_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('integer') {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <input
                          rdsInput
                          type="number"
                          inputmode="numeric"
                          [formControlName]="variable.name"
                          [attr.placeholder]="variable.placeholder"
                          [required]="variable.mandatory"
                          [attr.aria-label]="variable.label"
                        />
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_FIELD_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('date') {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <input
                          rdsInput
                          type="date"
                          [formControlName]="variable.name"
                          [attr.placeholder]="variable.placeholder"
                          [required]="variable.mandatory"
                          [attr.aria-label]="variable.label"
                        />
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_FIELD_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('datetime') {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <input
                          rdsInput
                          type="datetime-local"
                          [formControlName]="variable.name"
                          [attr.placeholder]="variable.placeholder"
                          [required]="variable.mandatory"
                          [attr.aria-label]="variable.label"
                        />
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_FIELD_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('reference') {
                      <rds-form-field
                        labelPosition="top"
                        [class.catalog-form__ref--readonly]="variable.readOnly"
                      >
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <rds-autocomplete
                          [formControlName]="variable.name"
                          [noDataText]="referenceNoDataText(variable.name)"
                          [options]="referenceOptionsFor(variable.name)"
                          [debounce]="referenceDebounceMs"
                          [minSearchChars]="referenceMinChars"
                          [required]="variable.mandatory"
                          displayProperty="display_value"
                          uniqProperty="sys_id"
                          [optionTemplate]="referenceOption"
                        ></rds-autocomplete>
                        <!-- Option row: display value + the ref_ac_columns extras SN
                             shows beside it (user_name · email for sys_user). Mirrors
                             RDS's default option so keyboard nav + highlight still work,
                             minus `size` / `isOptionDisabled` — the template context doesn't
                             expose them; neither is configured on these pickers. -->
                        <ng-template
                          #referenceOption
                          let-o="option"
                          let-ac="autocomplete"
                          let-focused="keyboardFocusedItem"
                        >
                          <button
                            type="button"
                            rds-single-select-list-item
                            [rdsAutocompleteOption]="ac"
                            [keyboardFocusedItem]="focused"
                            [checked]="ac.isSelected(o)"
                            (click)="ac.toggle(o)"
                          >
                            <span class="catalog-form__ref-option">
                              <span
                                [innerHTML]="ac.getDisplayProperty(o)"
                                [rdsAutocompleteHighlight]="ac.getSearchQuery()"
                              ></span>
                              @let detail = referenceOptionDetail(variable.name, o);
                              @if (detail) {
                                <span class="sr-only">, </span>
                                <span class="catalog-form__ref-option-detail">{{ detail }}</span>
                              }
                            </span>
                          </button>
                        </ng-template>
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        } @else {
                          <rds-control-hint>
                            Type at least {{ referenceMinChars }} characters to search.
                          </rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_CHOICE_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('reference_multi') {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <rds-multi-autocomplete
                          [formControlName]="variable.name"
                          [noDataText]="referenceNoDataText(variable.name)"
                          [options]="referenceOptionsFor(variable.name)"
                          [debounce]="referenceDebounceMs"
                          [minSearchChars]="referenceMinChars"
                          displayProperty="display_value"
                          uniqProperty="sys_id"
                          [required]="variable.mandatory"
                          [optionTemplate]="referenceMultiOption"
                        ></rds-multi-autocomplete>
                        <!-- Option row: display value + the ref_ac_columns extras SN
                             shows beside it (user_name · email for sys_user). Mirrors
                             RDS's default option so keyboard nav + highlight still work,
                             minus `size` / `isOptionDisabled` — the template context doesn't
                             expose them; neither is configured on these pickers. -->
                        <ng-template
                          #referenceMultiOption
                          let-o="option"
                          let-ac="autocomplete"
                          let-focused="keyboardFocusedItem"
                        >
                          <button
                            type="button"
                            rds-multi-select-list-item
                            [rdsAutocompleteOption]="ac"
                            [keyboardFocusedItem]="focused"
                            [checked]="ac.isSelected(o)"
                            (click)="ac.toggle(o)"
                          >
                            <span class="catalog-form__ref-option">
                              <span
                                [innerHTML]="ac.getDisplayProperty(o)"
                                [rdsAutocompleteHighlight]="ac.getSearchQuery()"
                              ></span>
                              @let detail = referenceOptionDetail(variable.name, o);
                              @if (detail) {
                                <span class="sr-only">, </span>
                                <span class="catalog-form__ref-option-detail">{{ detail }}</span>
                              }
                            </span>
                          </button>
                        </ng-template>
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        } @else {
                          <rds-control-hint>
                            Type at least {{ referenceMinChars }} characters to search;
                            picked names appear as chips inside the field.
                          </rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_MULTI_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('divider') {
                      <hr class="catalog-form__divider" />
                    }
                    @case ('masked') {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                        </rds-control-label>
                        <div class="catalog-form__masked">
                          <input
                            rdsInput
                            [type]="revealedMasked()[variable.name] ? 'text' : 'password'"
                            autocomplete="off"
                            [formControlName]="variable.name"
                            [attr.placeholder]="variable.placeholder"
                            [attr.maxlength]="variable.maxLength"
                            [required]="variable.mandatory"
                            [attr.aria-label]="variable.label"
                          />
                          <button
                            rds-text-button
                            size="s"
                            type="button"
                            [attr.aria-pressed]="!!revealedMasked()[variable.name]"
                            (click)="toggleMasked(variable.name)"
                          >
                            {{ revealedMasked()[variable.name] ? CATALOG_FORM_TEXTS.HIDE_VALUE : CATALOG_FORM_TEXTS.SHOW_VALUE }}
                          </button>
                        </div>
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_FIELD_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                    @case ('multiselect') {
                      <fieldset class="catalog-form__radio-field">
                        <legend class="catalog-form__radio-legend">
                          {{ variable.label }}
                          @if (variable.mandatory) {
                            <span class="catalog-form__required-mark" aria-hidden="true">*</span>
                            <span class="sr-only">{{ CATALOG_FORM_TEXTS.REQUIRED_SR }}</span>
                          }
                        </legend>
                        <div
                          class="catalog-form__multiselect"
                          [class.catalog-form__radio-group--across]="variable.choiceDirection === 'across'"
                        >
                          @for (choice of variable.choices; track choice.value) {
                            @if (choice.value !== '') {
                              <rds-checkbox
                                [checked]="isMultiChecked(variable.name, choice.value)"
                                [disabled]="variable.readOnly"
                                (change)="toggleMulti(variable.name, choice.value, $event.checked)"
                              >
                                {{ choice.label }}
                              </rds-checkbox>
                            }
                          }
                        </div>
                        @if (variable.helpText) {
                          <span class="catalog-form__hint" [innerHTML]="variable.helpText"></span>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <span class="catalog-form__checkbox-error" role="alert">
                            {{ CATALOG_FORM_TEXTS.REQUIRED_CHOICE_ERROR }}
                          </span>
                        }
                      </fieldset>
                    }
                    @default {
                      <rds-form-field labelPosition="top">
                        <rds-control-label>
                          {{ variable.label }}
                          @if (variable.tooltip) {
                            <button
                              type="button"
                              class="catalog-form__tooltip"
                              [rdsTooltip]="variable.tooltip"
                              [attr.aria-label]="CATALOG_FORM_TEXTS.MORE_INFO_SR + ': ' + variable.tooltip"
                            >
                              <rds-icon namespace="outlined" icon="info" aria-hidden="true" />
                            </button>
                          }
                        </rds-control-label>
                        <input
                          rdsInput
                          type="text"
                          [formControlName]="variable.name"
                          [attr.placeholder]="variable.placeholder"
                          [attr.maxlength]="variable.maxLength"
                          [required]="variable.mandatory"
                          [attr.aria-label]="variable.label"
                        />
                        @if (variable.helpText) {
                          <rds-control-hint><span [innerHTML]="variable.helpText"></span></rds-control-hint>
                        }
                        @if (
                          form.get(variable.name)?.touched &&
                          form.get(variable.name)?.hasError('required')
                        ) {
                          <rds-control-error>{{ CATALOG_FORM_TEXTS.REQUIRED_FIELD_ERROR }}</rds-control-error>
                        }
                      </rds-form-field>
                    }
                  }
                  @if (fieldMessages()[variable.name]; as msg) {
                    <rds-alert
                      class="catalog-form__field-msg"
                      [type]="msg.type === 'error' ? 'error' : 'info'"
                      [attr.role]="msg.type === 'error' ? 'alert' : 'status'"
                    >
                      {{ msg.text }}
                    </rds-alert>
                  }
                }
              </section>
            }
            @if (attachmentsAllowed()) {
              <inexp-catalog-attachments
                [disabled]="isSubmitting()"
                [required]="attachmentRequired()"
                [error]="attachmentError()"
                (filesChange)="onAttachmentsChange($event)"
              />
            }
          </div>

          <p class="catalog-form__disclaimer">
            {{ CATALOG_FORM_TEXTS.SENSITIVE_DATA_DISCLAIMER }}
          </p>

          @if (submitBlocked()) {
            <div class="catalog-form__state-card">
              <rds-alert type="error" role="alert">
                {{ CATALOG_FORM_TEXTS.SUBMIT_BLOCKED }}
              </rds-alert>
            </div>
          }
          @if (noOrder()) {
            <div class="catalog-form__state-card">
              <rds-alert type="info" role="status">{{ CATALOG_FORM_TEXTS.NO_ORDER }}</rds-alert>
            </div>
          }
          <div class="catalog-form__submit-container">
            <button
              rds-primary-button
              size="s"
              type="submit"
              [disabled]="isSubmitting() || noOrder()"
            >
              {{ isSubmitting() ? CATALOG_FORM_TEXTS.SUBMITTING : submitLabel() }}
            </button>
          </div>
        </form>
      }
    }
    </div>
  </section>

  <section class="catalog-form__right" aria-label="Offering information">
    <div class="catalog-form__info">
      @if (approvalBadgeLabel(); as badge) {
        <rds-badge
          size="m"
          [label]="badge"
          type="minimal"
        ></rds-badge>
      }
      <h2 class="catalog-form__offering-name">{{ offeringName() }}</h2>

      @if (licenseCostResponsibility()) {
        <p class="catalog-form__license">
          <strong>{{ CATALOG_FORM_TEXTS.LICENSE_COST_LABEL }}</strong>
          {{ licenseCostResponsibility() }}
        </p>
      } @else if (shortDescription()) {
        <p>{{ shortDescription() }}</p>
      }

      @if (itemDescription()) {
        <!-- r2f 1.1.3 item description (HTML); [innerHTML] sanitises. -->
        <div class="catalog-form__item-description" [innerHTML]="itemDescription()"></div>
      }

      @if (offeringDescription() || serviceBookUrl()) {
        <div class="catalog-form__details">
          @if (offeringDescription()) {
            <h3 class="catalog-form__details-heading">{{ CATALOG_FORM_TEXTS.DETAILS_HEADING }}</h3>
            <p class="catalog-form__details-text">{{ offeringDescription() }}</p>
          }
          @if (serviceBookUrl()) {
            <p class="catalog-form__service-book">
              <a [href]="serviceBookUrl()" target="_blank" rel="noopener">
                {{ CATALOG_FORM_TEXTS.SERVICE_BOOK_LINK }}
                <span class="sr-only">{{ CATALOG_FORM_TEXTS.NEW_TAB_SR }}</span>
              </a>
            </p>
          }
        </div>
      }
    </div>
  </section>
</main>
